import type { DocumentNode, TypedDocumentNode, ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { isNil } from 'lodash';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { Cursor } from '../types';
import { type CollectionResponse, type QueryVariables } from '../types';
import type { CollectionKey } from './types';
import { flattenCollections } from './util';

/**
 * Note: in order for paginated results to actually fetch more, you need to make sure
 * the collection is defined in the in memory cache in `packages/api/src/apollo.ts`
 */
export const usePaginatedQuery = <
  T extends Record<string, unknown>,
  V extends Record<string, unknown> = { first?: number; after?: string | null },
  K extends CollectionKey = CollectionKey,
>(
  query: DocumentNode | TypedDocumentNode<CollectionResponse<T, K>, QueryVariables<V>>,
  variables: V,
  collectionKey: K,
  skip?: boolean,
  first = 30,
  source?: 'auto-paginated',
) => {
  const apollo = useApolloClient();
  const [processedCursors, setProcessedCursors] = useState<{
    [k in Cursor]?: { hasLoaded: boolean };
  }>({});
  const [currentItems, setCurrentItems] = useState<T[]>([]);
  const [currentCursor, setCurrentCursor] = useState<Cursor | null>(null);
  const [hasMore, setHasMore] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>();
  const [error, setError] = useState<ApolloError | undefined>();
  const [loadedFirst, setLoadedFirst] = useState(false);

  const loadMore = useCallback(async () => {
    if ((hasMore !== undefined && !hasMore) || loading || skip) {
      return;
    }

    const lastCursor = currentCursor;

    const lasCursorRef = processedCursors[lastCursor as Cursor];

    if (lasCursorRef?.hasLoaded) {
      console.log('skipping bc already processed', { collectionKey, lastCursor });
      return;
    }

    setLoading(true);

    if (isNil(lasCursorRef)) {
      processedCursors[lastCursor as Cursor] = {
        hasLoaded: false,
      };
    }

    const newVars = {
      ...variables,
      first,
      after: currentCursor,
    };

    const { data: newData, error: newError } = await apollo.query<
      CollectionResponse<T, CollectionKey>,
      V
    >({
      query,
      variables: newVars,
    });

    const nextCursor = newData[collectionKey].pageInfo.endCursor;
    const nextHasMore = newData[collectionKey].pageInfo.hasNextPage;

    setHasMore(nextHasMore);
    setCurrentCursor(nextCursor);

    setError(newError);
    setCurrentItems((old) => {
      const newItems = newData[collectionKey].edges.map((edge) => flattenCollections(edge));
      return [...old, ...newItems.filter((item) => !old.some((i) => i.id === item.id))];
    });
    setProcessedCursors((o) => ({ ...o, [lastCursor as Cursor]: { hasLoaded: true } }));
    setLoading(false);
  }, [
    hasMore,
    loading,
    skip,
    currentCursor,
    processedCursors,
    collectionKey,
    apollo,
    query,
    variables,
    first,
  ]);

  useEffect(() => {
    if (loadedFirst === true) {
      return;
    }
    if (source !== 'auto-paginated') {
      void (async () => {
        await loadMore();
        setLoadedFirst(true);
      })();
    }
  }, [loadMore, loadedFirst, source]);

  return useMemo(
    () => ({
      items: currentItems,
      loading,
      error,
      hasMore,
      loadMore,
    }),
    [currentItems, error, hasMore, loadMore, loading],
  );
};

export const useAutoPaginatedQuery = <
  T extends Record<string, unknown>,
  V extends Record<string, unknown> = { first?: number; after?: string | null },
  K extends CollectionKey = CollectionKey,
>(
  query: DocumentNode | TypedDocumentNode<CollectionResponse<T, K>, QueryVariables<V>>,
  variables: V & { first?: number; after?: string | null },
  collectionKey: K,
  skip?: boolean,
) => {
  const { items, loading, error, hasMore, loadMore } = usePaginatedQuery(
    query,
    variables,
    collectionKey,
    skip,
    undefined,
    'auto-paginated',
  );

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    if (skip || (hasMore !== undefined && !hasMore) || loading) {
      return;
    }

    void loadMoreRef.current();
  }, [items.length, hasMore, loading, skip, collectionKey, items]);

  return useMemo(
    () => ({ items, loading: loading || hasMore, error }),
    [items, loading, hasMore, error],
  );
};
